/**
 * MODAL: CREATE SCHOOL
 * Modal pour créer une nouvelle école
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { CreateSchoolInput } from '../../schemas/school';
import { createSchoolSchema } from '../../schemas/school';
import { COUNTRIES, POSITIONINGS, SCHOOL_TYPES } from '../../types/school';
import { Form, FormInput, FormSelect, FormTextarea, FormPhoneInput } from '../ui';
import { Modal, ModalFooter } from '../ui/modal';

interface CreateSchoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSchoolInput) => Promise<void>;
}

export function CreateSchoolModal({ isOpen, onClose, onSubmit }: CreateSchoolModalProps) {
  const form = useForm<CreateSchoolInput>({
    resolver: zodResolver(createSchoolSchema),
    defaultValues: {
      name: '',
      postal_code: '',
      city: '',
      country: 'FRA',
      type: '',
      positioning: '',
      address: '',
      notes: '',
      contact_first_name: '',
      contact_last_name: '',
      contact_email: '',
      contact_phone: '',
    },
  });

  const handleFormSubmit = async (data: CreateSchoolInput) => {
    try {
      await onSubmit(data);
      form.reset();
      onClose();
    } catch (error) {
      console.error("Erreur lors de la création de l'école :", error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Créer une école"
      size="xl"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Créer"
          isSubmitting={form.formState.isSubmitting}
          formId="create-school-form"
        />
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} id="create-school-form">
          <div className="space-y-6">
            {/* Section Informations générales */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 mb-4">Informations générales</h3>

              <div className="space-y-4">
                {/* Nom & Type en 2 colonnes */}
                <div className="grid grid-cols-2 gap-4">
                  <FormInput control={form.control} name="name" label="Nom" required />

                  <FormSelect
                    control={form.control}
                    name="type"
                    label="Type"
                    placeholder="Sélectionner un type"
                    options={SCHOOL_TYPES.map((type) => ({ value: type, label: type }))}
                  />
                </div>

                {/* Positionnement */}
                <FormSelect
                  control={form.control}
                  name="positioning"
                  label="Positionnement"
                  placeholder="Sélectionner un positionnement"
                  options={POSITIONINGS.map((p) => ({ value: p, label: p }))}
                />
              </div>
            </div>

            {/* Section Personne à contacter */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 mb-4">Personne à contacter</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    control={form.control}
                    name="contact_first_name"
                    label="Prénom"
                    placeholder="Prénom"
                  />
                  <FormInput
                    control={form.control}
                    name="contact_last_name"
                    label="Nom"
                    placeholder="Nom"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    control={form.control}
                    name="contact_email"
                    label="Email"
                    type="email"
                    placeholder="email@exemple.com"
                  />
                  <FormPhoneInput control={form.control} name="contact_phone" label="Téléphone" />
                </div>
              </div>
            </div>

            {/* Section Adresse */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 mb-4">Adresse</h3>
              <div className="space-y-4">
                {/* Adresse complète */}
                <FormInput
                  control={form.control}
                  name="address"
                  label="Adresse"
                  placeholder="12 Av Trudaine"
                />

                {/* Ville, Code Postal, Pays en 3 colonnes */}
                <div className="grid grid-cols-3 gap-4">
                  <FormInput control={form.control} name="city" label="Ville" placeholder="Paris" />

                  <FormInput
                    control={form.control}
                    name="postal_code"
                    label="Code Postal"
                    placeholder="75009"
                  />

                  <FormSelect
                    control={form.control}
                    name="country"
                    label="Pays"
                    required
                    options={COUNTRIES.map((c) => ({ value: c.code, label: c.label }))}
                  />
                </div>

                {/* Notes */}
                <FormTextarea
                  control={form.control}
                  name="notes"
                  label="Notes"
                  placeholder="Ajouter une note..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </Modal>
  );
}
